import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import ErrorBoundary from "@/components/ErrorBoundary";
import EnvironmentBanner from "@shared/components/EnvironmentBanner";

import Home from "@user/pages/Home";
import Chat from "@user/pages/Chat";
import Lawyers from "@user/pages/Lawyers";
import LawyerDetail from "@user/pages/LawyerDetail";
import Articles from "@user/pages/Articles";
import ArticleDetail from "@user/pages/ArticleDetail";
import Dashboard from "@user/pages/Dashboard";
import AuthCallback from "@user/pages/AuthCallback";
import Booking from "@user/pages/Booking";
import ConsultationRoom from "@shared/consultation/ConsultationRoom";
import LawyerDashboard from "@user/pages/LawyerDashboard";
import BecomeALawyer from "@user/pages/BecomeALawyer";
import AdminApp from "@admin/AdminApp";
import LawyerApp from "@lawyer/LawyerApp";
import { ADMIN_BASE } from "@admin/constants/route";
import { LAWYER_BASE } from "@lawyer/constants/route";
import { IS_USER, IS_LAWYER, IS_ADMIN, IS_COMBINED, BUILD_TARGET } from "@/buildTarget";

/**
 * Combined-bundle routes (default behaviour for Emergent preview + dev).
 * Identical to the pre-Option-A app — every surface mounted under one Router.
 */
function CombinedRoutes() {
  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/chat" element={<Chat />} />
      <Route path="/chat/:id" element={<Chat />} />
      <Route path="/lawyers" element={<Lawyers />} />
      <Route path="/lawyers/:id" element={<LawyerDetail />} />
      <Route path="/book/:lawyerId" element={<Booking />} />
      <Route path="/consultations/:id" element={<ConsultationRoom />} />
      <Route path="/articles" element={<Articles />} />
      <Route path="/articles/:slug" element={<ArticleDetail />} />
      <Route path="/dashboard" element={<Dashboard />} />
      <Route path="/lawyer-dashboard" element={<LawyerDashboard />} />
      <Route path="/become-a-lawyer" element={<BecomeALawyer />} />
      <Route path={`${LAWYER_BASE}/*`} element={<LawyerApp />} />
      <Route path={`${ADMIN_BASE}/*`} element={<AdminApp />} />
      {/* Decoy: /admin/* silently redirects so admin URL isn't discoverable */}
      <Route path="/admin" element={<Home />} />
      <Route path="/admin/*" element={<Home />} />
    </Routes>
  );
}

/**
 * User-only routes (app.advoset.in). No lawyer or admin paths.
 * Consultation room is included because the user reaches it from /dashboard.
 */
function UserOnlyRoutes() {
  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/chat" element={<Chat />} />
      <Route path="/chat/:id" element={<Chat />} />
      <Route path="/lawyers" element={<Lawyers />} />
      <Route path="/lawyers/:id" element={<LawyerDetail />} />
      <Route path="/book/:lawyerId" element={<Booking />} />
      <Route path="/consultations/:id" element={<ConsultationRoom />} />
      <Route path="/articles" element={<Articles />} />
      <Route path="/articles/:slug" element={<ArticleDetail />} />
      <Route path="/dashboard" element={<Dashboard />} />
      <Route path="/become-a-lawyer" element={<BecomeALawyer />} />
      {/* Any unknown path → home */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

/**
 * Lawyer-only bundle (lawyers.advoset.in). LawyerApp expects to live at the
 * configured LAWYER_BASE. When deployed as a dedicated bundle we conventionally
 * set REACT_APP_LAWYER_PATH="" so LawyerApp mounts at the domain root.
 * Consultation room is shared (lawyers also use it).
 */
function LawyerOnlyRoutes() {
  return (
    <Routes>
      <Route path="/consultations/:id" element={<ConsultationRoom />} />
      <Route path={`${LAWYER_BASE || "/"}*`} element={<LawyerApp />} />
      <Route path="*" element={<Navigate to={LAWYER_BASE || "/"} replace />} />
    </Routes>
  );
}

/**
 * Admin-only bundle (admin.advoset.in). When deployed as a dedicated bundle
 * we conventionally set REACT_APP_ADMIN_PATH="" so AdminApp mounts at root.
 */
function AdminOnlyRoutes() {
  return (
    <Routes>
      <Route path={`${ADMIN_BASE || "/"}*`} element={<AdminApp />} />
      <Route path="*" element={<Navigate to={ADMIN_BASE || "/"} replace />} />
    </Routes>
  );
}

function AppRouter() {
  const location = useLocation();
  // OAuth callback hits the root with `#session_id=…`; preserve that in all builds.
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  let routes;
  if (IS_USER) routes = <UserOnlyRoutes />;
  else if (IS_LAWYER) routes = <LawyerOnlyRoutes />;
  else if (IS_ADMIN) routes = <AdminOnlyRoutes />;
  else routes = <CombinedRoutes />;  // includes IS_COMBINED + default

  return (
    <ErrorBoundary key={location.pathname}>
      {routes}
    </ErrorBoundary>
  );
}

function App() {
  // Print build target once in dev console so operators can confirm which
  // bundle is loaded when debugging in production.
  if (typeof window !== "undefined" && !window.__advosetBuildLogged) {
    /* eslint-disable no-console */
    console.info(`[advoset] build target: ${BUILD_TARGET}`);
    /* eslint-enable no-console */
    window.__advosetBuildLogged = true;
  }
  return (
    <div className="App" data-build={BUILD_TARGET}>
      <BrowserRouter>
        <AuthProvider>
          <EnvironmentBanner />
          <AppRouter />
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
